import graphql__345d7372e0e22be74cd6 from "./__generated__/AnalysisSimulationViewCatalogQuery.graphql.ts";
import graphql__cf8b1150c06c17a886c6 from "./__generated__/AnalysisSimulationViewQuery.graphql.ts";
import graphql__c9402ea1e48089a8d1d9 from "./__generated__/AnalysisSimulationViewScenariosQuery.graphql.ts";
import graphql__789bc10f31ba212edf25 from "./__generated__/AnalysisSimulationViewSaveScenarioMutation.graphql.ts";
import graphql__833af555a08936c60d18 from "./__generated__/AnalysisSimulationViewDeleteScenarioMutation.graphql.ts";import * as React from "react";
import {useEffect, useMemo, useRef, useState, useTransition} from "react";
import {useOutletContext} from "react-router";
import {usePreloadedQuery, useQueryLoader} from "react-relay/hooks";
import {graphql, useMutation} from "react-relay";
import {useIntl} from "react-intl";
import {formatAmount, formatCategoryLabel} from "../helper/revenueFormat";
import Panel from "./presentational/Panel";
import KpiCard from "./presentational/KpiCard";
import RevenueBreakdown from "./presentational/RevenueBreakdown";
import ActionButton from "./presentational/button/ActionButton";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faRotateLeft, faFloppyDisk, faFloppyDiskCircleArrowRight, faTrashCan, faSpinnerThird} from "@fortawesome/pro-regular-svg-icons";
import {periodForMonthsAgo, startDateForMonthsAgo} from "./AnalysisView";
import AnalysisSimulationViewQueryNode from "./__generated__/AnalysisSimulationViewQuery.graphql";
import AnalysisSimulationViewScenariosQueryNode from "./__generated__/AnalysisSimulationViewScenariosQuery.graphql";
import type {AnalysisSimulationViewQuery as AnalysisSimulationViewQueryType} from "./__generated__/AnalysisSimulationViewQuery.graphql";
import type {AnalysisSimulationViewCatalogQuery as AnalysisSimulationViewCatalogQueryType} from "./__generated__/AnalysisSimulationViewCatalogQuery.graphql";
import type {AnalysisSimulationViewScenariosQuery as AnalysisSimulationViewScenariosQueryType} from "./__generated__/AnalysisSimulationViewScenariosQuery.graphql";

const catalogQueryNode = graphql__345d7372e0e22be74cd6;

const simulationQuery = graphql__cf8b1150c06c17a886c6;

const scenariosQuery = graphql__c9402ea1e48089a8d1d9;

const saveScenarioMutation = graphql__789bc10f31ba212edf25;

const deleteScenarioMutation = graphql__833af555a08936c60d18;

const OVERRIDE_FIELDS = ['monthly', 'hourly', 'daily', 'monthlyCap', 'percentagePerProject', 'outboundNetworkTransferPerGigabyte', 'pricePerUnit'] as const;

type CatalogEntry = {
    readonly sku: string
    readonly label: string
    readonly type: string
    readonly platform: string | null
    readonly bookable: boolean
    readonly monthly: number | null
    readonly hourly: number | null
    readonly daily: number | null
    readonly monthlyCap: number | null
    readonly percentagePerProject: number | null
    readonly outboundNetworkTransferPerGigabyte: number | null
    readonly pricePerUnit: number | null
};

// Override values are kept in the API's units (cents for monetary fields, cents/unit for
// per-unit prices, percent for percentages), keyed by SKU then field.
type Overrides = Record<string, Record<string, number>>;

type FieldKind = 'euro' | 'cent' | 'percent';

type FieldSpec = {
    field: keyof CatalogEntry
    kind: FieldKind
};

// Which editable price fields each plan type exposes.
const FIELD_SPECS: Record<string, FieldSpec[]> = {
    instance: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}, {field: 'outboundNetworkTransferPerGigabyte', kind: 'cent'}],
    project: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}],
    cluster: [{field: 'monthly', kind: 'euro'}],
    clusterNode: [{field: 'daily', kind: 'euro'}, {field: 'monthlyCap', kind: 'euro'}],
    databaseServer: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}],
    redisServer: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}],
    elasticsearchServer: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}],
    grafanaInstance: [{field: 'monthly', kind: 'euro'}, {field: 'hourly', kind: 'cent'}],
    support: [{field: 'monthly', kind: 'euro'}, {field: 'percentagePerProject', kind: 'percent'}],
    meteredResource: [{field: 'pricePerUnit', kind: 'cent'}],
};

const TYPE_ORDER = ['instance', 'project', 'cluster', 'clusterNode', 'databaseServer', 'redisServer', 'elasticsearchServer', 'grafanaInstance', 'support', 'meteredResource'];

type AnalysisOutletContext = {
    catalogQuery: any
    simulationQuery: any
    scenariosQuery: any
};

const AnalysisSimulationView: React.FC = () => {
    const intl = useIntl();
    const {catalogQuery, simulationQuery: simulationQueryRef, scenariosQuery: scenariosQueryRef} = useOutletContext<AnalysisOutletContext>();

    const catalogData = usePreloadedQuery<AnalysisSimulationViewCatalogQueryType>(catalogQueryNode, catalogQuery);
    const catalog = catalogData.beachPricingCatalog as ReadonlyArray<CatalogEntry>;

    const [monthsAgo] = useState(0);
    const [overrides, setOverrides] = useState<Overrides>({});
    const [filter, setFilter] = useState('');
    const [scenarioName, setScenarioName] = useState('');

    const [simRef, loadSimulation] = useQueryLoader<AnalysisSimulationViewQueryType>(AnalysisSimulationViewQueryNode, simulationQueryRef);
    const result = usePreloadedQuery<AnalysisSimulationViewQueryType>(simulationQuery, simRef ?? simulationQueryRef).simulateSubscriptionRevenue;

    const [scenariosRef, loadScenarios] = useQueryLoader<AnalysisSimulationViewScenariosQueryType>(AnalysisSimulationViewScenariosQueryNode, scenariosQueryRef);
    const scenarios = usePreloadedQuery<AnalysisSimulationViewScenariosQueryType>(scenariosQuery, scenariosRef ?? scenariosQueryRef).priceSimulationScenarios;

    const [commitSave, savingScenario] = useMutation(saveScenarioMutation);
    const [commitDelete] = useMutation(deleteScenarioMutation);

    const [isPending, startTransition] = useTransition();
    const [scheduled, setScheduled] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const firstRun = useRef(true);

    const overrideCount = Object.values(overrides).filter(fields => Object.keys(fields).length > 0).length;

    const buildOverridesInput = () => Object.entries(overrides)
        .filter(([, fields]) => Object.keys(fields).length > 0)
        .map(([sku, fields]) => ({sku, ...fields}));

    // Re-run the simulation automatically (debounced) whenever the overrides change, so the
    // breakdown and difference always reflect the current prices without a manual trigger.
    useEffect(() => {
        if (firstRun.current) {
            firstRun.current = false;
            return;
        }
        setScheduled(true);
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            setScheduled(false);
            startTransition(() => {
                loadSimulation({...periodForMonthsAgo(monthsAgo), overrides: buildOverridesInput()}, {fetchPolicy: 'network-only'});
            });
        }, 500);
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overrides, monthsAgo]);

    const computing = scheduled || isPending;

    const refreshScenarios = () => loadScenarios({}, {fetchPolicy: 'network-only'});

    const trimmedName = scenarioName.trim();
    const willOverwrite = scenarios.some(scenario => scenario.name === trimmedName);

    const saveScenario = () => {
        if (trimmedName === '') {
            return;
        }
        commitSave({
            variables: {input: {name: trimmedName, overrides: buildOverridesInput()}},
            onCompleted: () => refreshScenarios(),
        });
    };

    const loadScenario = (scenario: {readonly name: string, readonly overrides: ReadonlyArray<Record<string, any>>}) => {
        const next: Overrides = {};
        for (const override of scenario.overrides) {
            const fields: Record<string, number> = {};
            for (const field of OVERRIDE_FIELDS) {
                const value = override[field];
                if (value !== null && value !== undefined) {
                    fields[field] = value as number;
                }
            }
            next[override.sku] = fields;
        }
        // Prefill the name so saving again overwrites this scenario instead of creating a copy.
        setScenarioName(scenario.name);
        setOverrides(next);
    };

    const deleteScenario = (id: string) => {
        commitDelete({
            variables: {input: {id}},
            onCompleted: () => refreshScenarios(),
        });
    };

    const resetOverrides = () => {
        setScenarioName('');
        setOverrides({});
    };

    const setOverride = (sku: string, field: string, apiValue: number | null) => {
        setOverrides(previous => {
            const next = {...previous};
            const fields = {...(next[sku] ?? {})};
            if (apiValue === null) {
                delete fields[field];
            } else {
                fields[field] = apiValue;
            }
            next[sku] = fields;
            return next;
        });
    };

    const monthLabel = intl.formatDate(startDateForMonthsAgo(monthsAgo), {year: 'numeric', month: 'long', timeZone: 'UTC'});

    // Only show plans that are bookable or were actually used (billed) in the baseline month.
    const usedSkus = new Set<string>(result.usedSkus ?? []);
    const grouped = useMemo(() => {
        const lowerFilter = filter.trim().toLowerCase();
        const byType: Record<string, CatalogEntry[]> = {};
        for (const entry of catalog) {
            if (!entry.bookable && !usedSkus.has(entry.sku)) {
                continue;
            }
            if (lowerFilter && !entry.label.toLowerCase().includes(lowerFilter) && !entry.sku.toLowerCase().includes(lowerFilter)) {
                continue;
            }
            (byType[entry.type] ??= []).push(entry);
        }
        for (const type of Object.keys(byType)) {
            byType[type].sort((a, b) => a.sku.localeCompare(b.sku));
        }
        return byType;
    }, [catalog, filter, result.usedSkus]);

    const simulated = result.simulated;

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-0">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">{intl.formatMessage({id: 'view.analysisSimulation.basis'}, {month: monthLabel})}</h2>
                <div className="flex items-center gap-3">
                    <span className={`flex items-center gap-2 text-xs text-gray-500 transition-opacity ${computing ? 'opacity-100' : 'opacity-0'}`}>
                        <FontAwesomeIcon icon={faSpinnerThird} spin/>
                        {intl.formatMessage({id: 'view.analysisSimulation.computing'})}
                    </span>
                    <ActionButton onClick={resetOverrides} icon={faRotateLeft} disabled={overrideCount === 0} label={intl.formatMessage({id: 'view.analysisSimulation.reset'})}/>
                </div>
            </div>

            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 transition-opacity ${computing ? 'opacity-50' : 'opacity-100'}`}>
                <KpiCard label={intl.formatMessage({id: 'view.analysisSimulation.kpi.baseline'})} value={formatAmount(result.baseline.total)} sub={monthLabel}/>
                <KpiCard label={intl.formatMessage({id: 'view.analysisSimulation.kpi.simulated'})} value={formatAmount(simulated.total)}/>
                <KpiCard
                    label={intl.formatMessage({id: 'view.analysisSimulation.kpi.delta'})}
                    value={`${result.delta > 0 ? '+' : ''}${formatAmount(result.delta)}`}
                    sub={result.deltaPercent === null || result.deltaPercent === undefined ? '—' : `${result.deltaPercent > 0 ? '+' : ''}${result.deltaPercent} %`}
                    trend={result.delta > 0 ? 'up' : result.delta < 0 ? 'down' : 'neutral'}
                />
            </div>

            <Panel headline={intl.formatMessage({id: 'view.analysisSimulation.scenarios'})}>
                <div className="flex items-center gap-2 mb-4">
                    <input
                        type="text"
                        value={scenarioName}
                        onChange={event => setScenarioName(event.target.value)}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                saveScenario();
                            }
                        }}
                        placeholder={intl.formatMessage({id: 'view.analysisSimulation.scenarios.namePlaceholder'})}
                        className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
                    />
                    <ActionButton
                        onClick={saveScenario}
                        icon={faFloppyDisk}
                        isSubmitting={savingScenario}
                        disabled={trimmedName === '' || overrideCount === 0}
                        label={willOverwrite ? intl.formatMessage({id: 'view.analysisSimulation.scenarios.overwrite'}) : intl.formatMessage({id: 'view.analysisSimulation.scenarios.save'})}
                        title={willOverwrite ? intl.formatMessage({id: 'view.analysisSimulation.scenarios.overwriteTitle'}, {name: trimmedName}) : intl.formatMessage({id: 'view.analysisSimulation.scenarios.saveTitle'})}
                    />
                </div>
                {scenarios.length === 0 ? (
                    <p className="text-sm text-gray-400">{intl.formatMessage({id: 'view.analysisSimulation.scenarios.empty'})}</p>
                ) : (
                    <ul className="divide-y divide-gray-100">
                        {scenarios.map(scenario => (
                            <li key={scenario.id} className="flex items-center justify-between py-2 group">
                                <div className="min-w-0">
                                    <div className="text-sm text-gray-900 truncate">{scenario.name}</div>
                                    <div className="text-xs text-gray-400">
                                        {intl.formatDate(scenario.createdAt, {year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'})}
                                        {' · '}
                                        {intl.formatMessage({id: 'view.analysisSimulation.scenarios.overrideCount'}, {count: scenario.overrides.length})}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0 invisible group-hover:visible">
                                    <button
                                        type="button"
                                        onClick={() => loadScenario(scenario)}
                                        title={intl.formatMessage({id: 'view.analysisSimulation.scenarios.load'})}
                                        className="p-1.5 text-gray-400 hover:text-gray-700"
                                    >
                                        <FontAwesomeIcon icon={faFloppyDiskCircleArrowRight}/>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deleteScenario(scenario.id)}
                                        title={intl.formatMessage({id: 'view.analysisSimulation.scenarios.delete'})}
                                        className="p-1.5 text-gray-400 hover:text-red-600"
                                    >
                                        <FontAwesomeIcon icon={faTrashCan}/>
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                <Panel headline={intl.formatMessage({id: 'view.analysisSimulation.editor'})}>
                    <input
                        type="text"
                        value={filter}
                        onChange={event => setFilter(event.target.value)}
                        placeholder={intl.formatMessage({id: 'view.analysisSimulation.filter'})}
                        className="mb-4 w-full border border-gray-300 rounded px-3 py-1.5 text-sm"
                    />
                    <div className="space-y-6">
                        {TYPE_ORDER.filter(type => grouped[type]?.length).map(type => {
                            const groupSpecs = (FIELD_SPECS[type] ?? []).filter(spec => grouped[type].some(entry => (entry[spec.field] as number | null) !== null));
                            return (
                                <div key={type}>
                                    <div className="flex items-end justify-between gap-3 mb-1">
                                        <h3 className="text-xs font-semibold text-gray-500 uppercase">{intl.formatMessage({id: `planType.${type}`})}</h3>
                                        <div className="flex items-center gap-2 shrink-0">
                                            {groupSpecs.map(spec => (
                                                <span key={spec.field as string} className="flex items-center gap-1">
                                                    <span className="w-20 text-right text-[11px] font-medium uppercase tracking-wide text-gray-400">{intl.formatMessage({id: `priceField.${spec.field as string}`})}</span>
                                                    <span className="w-8 text-[11px] text-gray-300">{UNIT_LABEL[spec.kind]}</span>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        {grouped[type].map(entry => (
                                            <PlanRow key={entry.sku} entry={entry} specs={groupSpecs} overrides={overrides[entry.sku] ?? {}} onChange={setOverride} billed={usedSkus.has(entry.sku)}/>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </Panel>

                <div className={`space-y-6 transition-opacity ${computing ? 'opacity-50' : 'opacity-100'}`}>
                    <RevenueBreakdown
                        title={intl.formatMessage({id: 'view.analysisIncome.byCustomer'})}
                        entries={simulated.byCustomerAccount}
                        total={simulated.total}
                        emptyLabel={intl.formatMessage({id: 'view.analysisIncome.empty'})}
                        showDelta
                    />
                    <RevenueBreakdown
                        title={intl.formatMessage({id: 'view.analysisIncome.byCategory'})}
                        entries={simulated.byCostCategory}
                        total={simulated.total}
                        emptyLabel={intl.formatMessage({id: 'view.analysisIncome.empty'})}
                        formatLabel={formatCategoryLabel}
                        showDelta
                    />
                </div>
            </div>
        </div>
    );
};

type PlanRowProps = {
    entry: CatalogEntry
    specs: FieldSpec[]
    overrides: Record<string, number>
    onChange: (sku: string, field: string, apiValue: number | null) => void
    /** Whether this plan was actually billed in the base month; if not, editing it has no effect. */
    billed: boolean
};

// Conversions between the input's display unit and the API unit.
const toDisplay = (kind: FieldKind, apiValue: number): string => kind === 'euro' ? String(apiValue / 100) : String(apiValue);
const toApi = (kind: FieldKind, displayValue: string): number | null => {
    if (displayValue.trim() === '') {
        return null;
    }
    const parsed = Number(displayValue.replace(',', '.'));
    if (Number.isNaN(parsed)) {
        return null;
    }
    return kind === 'euro' ? Math.round(parsed * 100) : parsed;
};
const UNIT_LABEL: Record<FieldKind, string> = {euro: '€', cent: 'ct', percent: '%'};
const PLATFORM_LABELS: Record<string, string> = {
    HETZNER_CLOUD: 'Hetzner Cloud',
    HETZNER_DEDICATED: 'Bare Metal',
    GOOGLE_CLOUD: 'Google Cloud',
    OPEN_TELEKOM_CLOUD: 'OTC',
};

const PlanRow: React.FC<PlanRowProps> = ({entry, specs, overrides, onChange, billed}) => {
    const intl = useIntl();
    if (specs.length === 0) {
        return null;
    }
    return (
        <div className={`flex items-start justify-between gap-3 ${billed ? '' : 'opacity-60'}`}>
            <div className="min-w-0">
                <div className="text-sm text-gray-900 truncate">
                    {entry.label}
                    {entry.platform && <span className="text-gray-400"> · {PLATFORM_LABELS[entry.platform] ?? entry.platform}</span>}
                    {!entry.bookable && <span className="ml-1 text-xs text-amber-600">({intl.formatMessage({id: 'view.analysisSimulation.expired'})})</span>}
                    {!billed && <span className="ml-1 text-xs text-gray-400">({intl.formatMessage({id: 'view.analysisSimulation.notBilled'})})</span>}
                </div>
                <div className="text-xs text-gray-400 font-mono truncate">{entry.sku}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
                {specs.map(spec => {
                    const catalogValue = entry[spec.field] as number | null;
                    // Keep columns aligned even when a plan does not offer this particular price.
                    if (catalogValue === null) {
                        return (
                            <span key={spec.field as string} className="flex items-center gap-1">
                                <span className="w-20"/>
                                <span className="w-8"/>
                            </span>
                        );
                    }
                    const overrideApi = overrides[spec.field as string];
                    const displayValue = overrideApi !== undefined ? toDisplay(spec.kind, overrideApi) : '';
                    return (
                        <span key={spec.field as string} className="flex items-center gap-1">
                            <input
                                type="number"
                                step="any"
                                value={displayValue}
                                placeholder={toDisplay(spec.kind, catalogValue)}
                                onChange={event => onChange(entry.sku, spec.field as string, toApi(spec.kind, event.target.value))}
                                className="w-20 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                            />
                            <span className="text-xs text-gray-400 w-8">{UNIT_LABEL[spec.kind]}</span>
                        </span>
                    );
                })}
            </div>
        </div>
    );
};

export default AnalysisSimulationView;
